import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/unifiedUserService';

/**
 * Custom hook for admin authentication and permissions
 */
export function useAdmin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user?.userId) {
      setIsAdmin(false);
      setAdminPermissions([]);
      setLoading(false);
      return;
    }

    try {
      const result = await userService.getUser(user.userId);
      
      // Enhanced debugging for frank.danielj@gmail.com issue
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 useAdmin Debug:', {
          userEmail: user.username || user.email,
          userId: user.userId,
          serviceResult: result,
          userData: result?.data
        });
      }
      
      if (result.success && result.data) {
        const userData = result.data;
        const adminStatus = userData.role === 'admin' || userData.isAdmin === true;
        const permissions = userData.adminPermissions || [];
        
        // Extra debugging for admin status determination
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Admin Status Calculation:', {
            userEmail: user.username || user.email,
            role: userData.role,
            isAdmin: userData.isAdmin,
            calculatedAdminStatus: adminStatus,
            permissions
          });
        }
        
        setIsAdmin(adminStatus);
        setAdminPermissions(permissions);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 No user data found:', {
            userEmail: user.username || user.email,
            result
          });
        }
        setIsAdmin(false);
        setAdminPermissions([]);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setAdminPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    isAdmin,
    adminPermissions,
    loading,
    hasPermission: (permission) => adminPermissions.includes(permission),
    requireAdmin: () => {
      if (!isAdmin) throw new Error('Admin access required');
    },
    requirePermission: (permission) => {
      if (!adminPermissions.includes(permission)) {
        throw new Error(`Permission required: ${permission}`);
      }
    },
    refreshAdminStatus: checkAdminStatus
  };
}

export default useAdmin;
